import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  CheckIcon,
  CommandLineIcon,
  ExclamationTriangleIcon,
  InboxIcon,
  PlusIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import { contextService } from '../../services/api';

const intentTone = {
  schedule: 'status-pill-info',
  prepare: 'status-pill-success',
  remember: 'status-pill-warning',
  capture: 'bg-[#edf0ed] text-[#637068]',
};

const InboxPage = () => {
  const queryClient = useQueryClient();
  const [assignments, setAssignments] = useState({});

  const inboxQuery = useQuery({
    queryKey: ['inbox'],
    queryFn: () => contextService.getInbox(),
  });
  const contextsQuery = useQuery({
    queryKey: ['contexts', 'inbox'],
    queryFn: () => contextService.getContexts(),
  });

  const items = inboxQuery.data?.items || [];
  const contexts = contextsQuery.data?.contexts || [];

  const resolveMutation = useMutation({
    mutationFn: ({ itemId, resolution }) => contextService.resolveInboxItem(itemId, resolution),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['today'] });
      queryClient.invalidateQueries({ queryKey: ['contexts'] });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      setAssignments((current) => {
        const next = { ...current };
        delete next[variables.itemId];
        return next;
      });
      toast.success(variables.resolution.action === 'dismiss' ? 'Capture dismissed' : 'Added to context');
    },
    onError: (resolveError) => toast.error(resolveError.response?.data?.detail || 'Could not resolve this capture'),
  });

  const resolveIntoContext = (itemId) => {
    const contextId = assignments[itemId];
    if (!contextId) {
      toast.error('Choose a context first');
      return;
    }
    resolveMutation.mutate({ itemId, resolution: { action: 'link', context_id: Number(contextId) } });
  };

  return (
    <div className="app-page">
      <div className="app-page-inner max-w-5xl">
        <header className="page-header">
          <div>
            <div className="eyebrow">
              <InboxIcon className="h-4 w-4" />
              Unresolved context
            </div>
            <h1 className="page-title">Inbox</h1>
            <p className="page-description">
              Review information Timelyzer captured but could not classify confidently. Nothing here changes your
              schedule until you decide where it belongs.
            </p>
          </div>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('timelyzer:universal-capture'))}
            className="primary-button"
          >
            <PlusIcon className="h-4 w-4" />
            Ask or capture
          </button>
        </header>

        <div className="mb-4 flex flex-col gap-3 rounded-lg border border-[#d9e2dc] bg-[#f1f7f3] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#246b52]" />
            <div>
              <p className="text-sm font-bold">Review-first classification</p>
              <p className="mt-1 text-xs leading-5 text-[#607068]">
                Confidence is visible, source material is preserved, and dismissing an item does not delete its source.
              </p>
            </div>
          </div>
          <span className="status-pill status-pill-warning shrink-0">{items.length} unresolved</span>
        </div>

        {inboxQuery.isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((item) => <div key={item} className="h-48 animate-pulse rounded-lg bg-[#e8ece8]" />)}
          </div>
        ) : inboxQuery.error ? (
          <section className="surface-panel px-6 py-16 text-center">
            <ExclamationTriangleIcon className="mx-auto h-8 w-8 text-[#c65f43]" />
            <h2 className="mt-3 text-base font-bold">Inbox unavailable</h2>
            <button onClick={() => inboxQuery.refetch()} className="secondary-button mt-5">Try again</button>
          </section>
        ) : items.length === 0 ? (
          <section className="surface-panel px-6 py-20 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-[#dcece4] text-[#246b52]">
              <CheckIcon className="h-6 w-6" />
            </span>
            <h2 className="mt-4 text-base font-bold">Inbox is clear</h2>
            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-[#6b766f]">
              Timelyzer has no unresolved captures waiting for classification.
            </p>
          </section>
        ) : (
          <div className="space-y-3">
            {items.map((item) => {
              const isPending = resolveMutation.isPending && resolveMutation.variables?.itemId === item.id;
              return (
                <article key={item.id} className="surface-panel overflow-hidden">
                  <div className="grid gap-5 p-5 md:grid-cols-[minmax(0,1fr)_260px] md:p-6">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`status-pill ${intentTone[item.inferred_intent] || intentTone.capture}`}>
                          <SparklesIcon className="h-3.5 w-3.5" />
                          {item.inferred_intent}
                        </span>
                        <span className="text-xs font-semibold text-[#7a857e]">{Math.round(item.confidence * 100)}% confidence</span>
                        <span className="text-xs text-[#8a948e]">· {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}</span>
                      </div>
                      {item.title && <h2 className="mt-4 text-base font-bold">{item.title}</h2>}
                      <blockquote className="mt-4 border-l-2 border-[#cbd8d0] pl-4 text-base font-semibold leading-7 text-[#344139]">
                        {item.content}
                      </blockquote>
                      <p className="mt-4 flex items-center gap-2 text-xs font-semibold text-[#6d7871]">
                        <CommandLineIcon className="h-4 w-4" />
                        {item.suggested_action}
                      </p>
                    </div>

                    <div className="border-t border-[#e2e7e3] pt-4 md:border-l md:border-t-0 md:pl-5 md:pt-0">
                      <label htmlFor={`context-${item.id}`} className="field-label">Belongs to</label>
                      <select
                        id={`context-${item.id}`}
                        value={assignments[item.id] || ''}
                        onChange={(event) => setAssignments((current) => ({ ...current, [item.id]: event.target.value }))}
                        className="field-input"
                      >
                        <option value="">Choose a context</option>
                        {contexts.map((context) => (
                          <option key={context.id} value={context.id}>{context.title}</option>
                        ))}
                      </select>
                      {contexts.length === 0 && (
                        <p className="mt-2 text-xs leading-5 text-[#7a857e]">
                          Create a context before resolving this item.
                        </p>
                      )}
                      <div className="mt-4 grid gap-2">
                        <button
                          onClick={() => resolveIntoContext(item.id)}
                          disabled={isPending || !assignments[item.id]}
                          className="primary-button w-full disabled:opacity-50"
                        >
                          {isPending ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                          Add to context
                        </button>
                        <button
                          onClick={() => resolveMutation.mutate({ itemId: item.id, resolution: { action: 'dismiss' } })}
                          disabled={isPending}
                          className="secondary-button w-full text-[#8b4938]"
                        >
                          <TrashIcon className="h-4 w-4" />
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {contexts.length === 0 && items.length > 0 && (
          <div className="mt-4 flex items-center justify-between rounded-lg border border-[#dce3dd] bg-white p-4">
            <div>
              <p className="text-sm font-bold">No contexts exist yet</p>
              <p className="mt-1 text-xs text-[#6f7a73]">Create the situation where these captures belong.</p>
            </div>
            <Link to="/contexts" className="secondary-button">Create context</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxPage;
